import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select, { StylesConfig } from "react-select";
import { Country, State } from "country-state-city";
import { useTheme } from "../../contexts/ThemeContext";
import { useCreateOrganisationMutation } from "../../services";
import { toast } from "../../core/toast";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { adminRoutes } from "../../routers/AdminRouters";
import PhoneInputField from "../../components/PhoneInputField";

type Option = { value: string; label: string; countryCode?: string };

// Extended form state to include new design fields (displayName, taxId)
interface ExtendedFormState {
  name: string;
  displayName: string;
  phone: string;
  address: string;
  taxId: string;
  city: string;
  state: string;
  stateCode: string;
  country: string;
  countryCode: string;
  postal: string;
}

interface FormErrors {
  name?: string;
  displayName?: string;
  phone?: string;
  address?: string;
  country?: string;
  state?: string;
  city?: string;
  postal?: string;
}

const OrganisationCreate: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [createOrganisation, { isLoading, isError }] =
    useCreateOrganisationMutation();

  const [form, setForm] = useState<ExtendedFormState>({
    name: "",
    displayName: "",
    // email: "",
    phone: "",
    address: "",
    taxId: "",
    // currency: "",
    city: "",
    state: "",
    stateCode: "",
    country: "",
    countryCode: "",
    postal: "",
  });

  // inline errors
  const [errors, setErrors] = useState<FormErrors>({});

  // THEME CLASSES (match AdminCreate look/feel)
  const pageBg = theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50";
  const titleText = theme === "dark" ? "text-gray-100" : "text-gray-900";
  const crumbText = theme === "dark" ? "text-gray-400" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";
  const cardClass =
    theme === "dark"
      ? "bg-[#0F0F0F] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";
  const labelClass = theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700";
  const inputBase =
    "w-full rounded-md px-3 py-2 outline-none transition-colors";
  const inputClass =
    theme === "dark"
      ? `${inputBase} bg-[#111111] border border-[#2C2C2C] placeholder-gray-500 focus:border-[#B6CB24] text-gray-100`
      : `${inputBase} bg-white border border-gray-300 placeholder-gray-500 focus:border-[#B6CB24] text-gray-900`;

  // Options
  const countryOptions: Option[] = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.isoCode,
        label: `${c.name} - ${c.isoCode}`,
      })),
    []
  );

  const allStates: Option[] = useMemo(
    () =>
      State.getAllStates().map((s) => ({
        value: `${s.countryCode}:${s.isoCode}`,
        label: s.name,
        countryCode: s.countryCode,
      })),
    []
  );

  const currencyOptions: Option[] = useMemo(
    () => [
      { value: "USA", label: "USA - US Dollar" },
      { value: "INR", label: "INR - Indian Rupee" },
      { value: "AED", label: "AED - United Arab Emirates Dirham" },
    ],
    []
  );

  const statesForSelectedCountry = useMemo(() => {
    if (!form.countryCode) return allStates;
    return allStates.filter((s) => s.countryCode === form.countryCode);
  }, [form.countryCode, allStates]);

  // react-select theme-aware styles
  const selectStyles: StylesConfig<Option, false> = useMemo(() => {
    const isDark = theme === "dark";
    const bg = isDark ? "#111111" : "#ffffff";
    const menuBg = isDark ? "#111111" : "#ffffff";
    const border = isDark ? "#2C2C2C" : "#d1d5db";
    const hoverBorder = "#B6CB24";
    const focusedBg = isDark ? "#1E1E1E" : "#f3f4f6";
    const text = isDark ? "#e5e7eb" : "#111827";
    const subText = isDark ? "#9ca3af" : "#6b7280";

    return {
      control: (base, state) => ({
        ...base,
        backgroundColor: bg,
        borderColor: state.isFocused ? hoverBorder : border,
        boxShadow: "none",
        minHeight: 40,
        ":hover": { borderColor: state.isFocused ? hoverBorder : border },
      }),
      menu: (base) => ({ ...base, backgroundColor: menuBg, zIndex: 50 }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? focusedBg : menuBg,
        color: text,
        cursor: "pointer",
      }),
      singleValue: (base) => ({ ...base, color: text }),
      placeholder: (base) => ({ ...base, color: subText }),
      input: (base) => ({ ...base, color: text }),
      valueContainer: (base) => ({ ...base, paddingInline: 8 }),
      dropdownIndicator: (base) => ({ ...base, color: subText }),
      clearIndicator: (base) => ({ ...base, color: subText }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: border }),
    };
  }, [theme]);

  const handleTextChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    // phone: allow digits and + sign
    if (name === "phone") {
      const cleaned = value.replace(/[^\d+]/g, "");
      setForm((f) => ({ ...f, phone: cleaned }));
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));

    // clear errors while typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // const handleEmailBlur = () => {
  //   const email = form.email.trim();
  //   if (!email) {
  //     setErrors((prev) => ({ ...prev, email: undefined }));
  //     return;
  //   }
  //   setErrors((prev) => ({
  //     ...prev,
  //     email: !emailRegex.test(email) ? "Email is invalid" : undefined,
  //   }));
  // };

  const onCountryChange = (opt: Option | null) => {
    if (!opt) {
      setForm((f) => ({
        ...f,
        country: "",
        countryCode: "",
        state: "",
        stateCode: "",
      }));
      return;
    }
    setForm((f) => ({
      ...f,
      country: opt.label,
      countryCode: opt.value,
      state: f.countryCode === opt.value ? f.state : "",
      stateCode: f.countryCode === opt.value ? f.stateCode : "",
    }));
    if (errors.country) setErrors((prev) => ({ ...prev, country: undefined }));
  };

  const onStateChange = (opt: Option | null) => {
    if (!opt) {
      setForm((f) => ({ ...f, state: "", stateCode: "" }));
      return;
    }
    const [countryCode, stateCode] = String(opt.value).split(":");
    const country = Country.getAllCountries().find(
      (c) => c.isoCode === countryCode
    );
    setForm((f) => ({
      ...f,
      state: opt.label,
      stateCode,
      country: country?.name || f.country,
      countryCode: country?.isoCode || f.countryCode,
    }));
    if (errors.state) setErrors((prev) => ({ ...prev, state: undefined }));
  };

  const onCurrencyChange = (opt: Option | null) => {
    setForm((f) => ({ ...f, currency: opt?.value || "" }));
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!String(form.name || "").trim()) newErrors.name = "Organization Legal Name is required";
    // displayName is optional
    if (!String(form.phone || "").trim()) newErrors.phone = "Phone Number is required";
    if (!form.countryCode) newErrors.country = "Country is required";
    if (!form.stateCode) newErrors.state = "State is required";
    if (!String(form.city || "").trim()) newErrors.city = "City is required";
    if (!String(form.postal || "").trim()) newErrors.postal = "Postal / Zip Code is required";
    if (!String(form.address || "").trim()) newErrors.address = "Billing Address is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill all mandatory fields marked with *");
    }
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload: any = {
        name: String(form.name || "").trim(),
        displayName: String(form.displayName || "").trim(),
        phone: String(form.phone || "").trim(),
        address: String(form.address || "").trim(),
        taxId: String(form.taxId || "").trim(),
        city: String(form.city || "").trim(),
        state: String(form.state || "").trim(),
        country: form.countryCode,
        postal: String(form.postal || "").trim(),
      };

      await createOrganisation(payload).unwrap();
      navigate(adminRoutes.organisationList);
    } catch (err: any) {
      console.error("Failed to create organisation:", err);
    }
  };

  const selectedCountry = form.countryCode
    ? { value: form.countryCode, label: form.country }
    : null;

  const selectedState =
    form.stateCode && form.countryCode
      ? { value: `${form.countryCode}:${form.stateCode}`, label: form.state }
      : form.state
        ? { value: form.state, label: form.state }
        : null;

  // const selectedCurrency =
  //   form.currency && currencyOptions.find((c) => c.value === form.currency)
  //     ? currencyOptions.find((c) => c.value === form.currency) || null
  //     : null;

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-semibold ${titleText}`}>
            Organization Overview
          </h1>
          <div className="text-sm mt-1">
            <Link to={adminRoutes.organisationList} className={crumbText}>
              Dashboard
            </Link>
            <span className={crumbSep}> &nbsp;&gt;&nbsp; </span>
            <span className="text-[#B6CB24] font-medium">
              Create Organization
            </span>
          </div>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className={`${cardClass} rounded-lg p-2 md:p-4`}
      >
        <h2 className="text-lg font-semibold mb-2 border-b pb-1 border-gray-700">
          Create New Organization
        </h2>

        {isError && (
          <div className="mb-4 p-3 rounded-md bg-red-500/10 border border-red-500/50 text-red-500">
            Failed to create organisation. Please try again.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Organization Legal Name <span className="text-red-500">*</span>
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={`${inputClass} ${errors.name ? "border-red-500" : ""}`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>
          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Display Name (If different legal company name)
            </label>
            <input
              name="displayName"
              value={form.displayName}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
          <PhoneInputField
            label="Phone Number"
            required
            value={form.phone}
            onChange={(value) => {
              const cleanValue = value.replace(/\D/g, ""); // 👈 remove hyphens, spaces, etc.

              setForm((f) => ({ ...f, phone: cleanValue }));

              if (errors.phone)
                setErrors((prev) => ({ ...prev, phone: undefined }));
            }}
            error={errors.phone}
            theme={theme}

          />
        </div>

        {/* <div className="flex flex-col md:flex-row gap-6 mb-2">
          <div className="flex-grow basis-0 md:max-w-xs space-y-4">
            <div>
              <label className={`block text-sm mb-2 ${labelClass}`}>
                Tax ID (Optional)
              </label>
              <input
                name="taxId"
                value={form.taxId}
                onChange={handleTextChange}
                placeholder="Enter here"
                className={inputClass}
              />
            </div>
            <div>
              <label className={`block text-sm mb-2 ${labelClass}`}>
                Choose Currency
              </label>
              <Select<Option, false>
                instanceId="currency-select"
                styles={selectStyles}
                isClearable
                placeholder="Choose from list"
                options={currencyOptions}
                value={selectedCurrency}
                onChange={onCurrencyChange}
                classNamePrefix="rs"
              />
            </div>
          </div>
          <div className="flex-grow-[2] basis-0">
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Billing Address
            </label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={`${inputClass} min-h-[120px] resize-y`}
            />
          </div>
        </div> */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-8">
          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Country <span className="text-red-500">*</span>
            </label>
            <Select<Option, false>
              instanceId="country-select"
              styles={{
                ...selectStyles,
                control: (base, state) => ({
                  ...selectStyles.control!(base, state),
                  borderColor: errors.country ? "#ef4444" : selectStyles.control!(base, state).borderColor,
                })
              }}
              isClearable
              isSearchable
              placeholder="Choose from list"
              options={countryOptions}
              value={selectedCountry}
              onChange={onCountryChange}
              classNamePrefix="rs"
            />
            {errors.country && (
              <p className="text-red-500 text-xs mt-1">{errors.country}</p>
            )}
          </div>

          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>State <span className="text-red-500">*</span></label>
            <Select<Option, false>
              instanceId="state-select"
              styles={{
                ...selectStyles,
                control: (base, state) => ({
                  ...selectStyles.control!(base, state),
                  borderColor: errors.state ? "#ef4444" : selectStyles.control!(base, state).borderColor,
                })
              }}
              isClearable
              isSearchable
              placeholder="Choose from list"
              options={statesForSelectedCountry}
              value={selectedState}
              onChange={onStateChange}
              classNamePrefix="rs"
            />
            {errors.state && (
              <p className="text-red-500 text-xs mt-1">{errors.state}</p>
            )}
          </div>

          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>City <span className="text-red-500">*</span></label>
            <input
              name="city"
              value={form.city}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={`${inputClass} ${errors.city ? "border-red-500" : ""}`}
            />
            {errors.city && (
              <p className="text-red-500 text-xs mt-1">{errors.city}</p>
            )}
          </div>

          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Postal / Zip Code <span className="text-red-500">*</span>
            </label>
            <input
              name="postal"
              value={form.postal}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={`${inputClass} ${errors.postal ? "border-red-500" : ""}`}
            />
            {errors.postal && (
              <p className="text-red-500 text-xs mt-1">{errors.postal}</p>
            )}
          </div>
          <div>
            <label className={`block text-sm mb-2 ${labelClass}`}>
              Tax ID (Optional)
            </label>
            <input
              name="taxId"
              value={form.taxId}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
        </div>
        <div className="flex-grow-[2] basis-0">
          <label className={`block text-sm mb-2 ${labelClass}`}>
            Billing Address <span className="text-red-500">*</span>
          </label>
          <textarea
            name="address"
            value={form.address}
            onChange={handleTextChange}
            placeholder="Enter here"
            className={`${inputClass} min-h-[120px] resize-y ${errors.address ? "border-red-500" : ""}`}
          />
          {errors.address && (
            <p className="text-red-500 text-xs mt-1">{errors.address}</p>
          )}
        </div>

        {/* section divider above action buttons */}
        <div
          className={
            theme === "dark"
              ? "border-t border-[#2C2C2C] my-4"
              : "border-t border-gray-200 my-4"
          }
        />

        <div className="flex items-center gap-4 mt-2">
          <button
            type="submit"
            disabled={isLoading}
            className={`px-8 py-1.5 rounded-md font-medium ${theme === "dark"
              ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`} style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
          >
            {isLoading ? "Creating..." : "Create"}
          </button>
          <Link
            to={`${adminRoutes.organisationList}`}
            className={`px-8 py-1.5 rounded-[10px] border border-gray-600 font-medium ${theme === "dark"
              ? "bg-transparent text-gray-300 hover:bg-[#2C2C2C]"
              : "bg-transparent text-gray-700 hover:bg-gray-100"
              }`}
          >
            Not Now
          </Link>
        </div>
      </form>
    </div>
  );
};

export default OrganisationCreate;
